// The app's one read path: the store, the overlay over it, and the signal that
// says re-read (#207).
//
// ── One of each, per tab ─────────────────────────────────────────────────
//
// Built on first use, as `writes.js` builds the queue, for two reasons:
// nothing should open an IndexedDB database at import time, and there must be
// **one** store instance per tab rather than one per provider. `appStore`'s
// `fresh` state means "a snapshot landed in this session", and a session *is*
// a store instance; a store per provider would make a reload
// indistinguishable from a first load for every one of them.
//
// ── The reviver, which is one decision in two places ─────────────────────
//
// `appStore` stores an instant as an ISO string and hands it back through
// `reviveTimestamp`; `overlay` defaults to the store's own so that a document
// with a queued write and a document without one come back with the same
// representation of the same instant (#265). The app's answer is a real
// Firestore `Timestamp`, because that is what the providers' `normalise*`
// functions receive from `onSnapshot`, and this path must hand them the same
// shape.
//
// This is the file that imports the SDK for it. `appStore.js` and
// `docShape.js` do not — the `ui` jest project has no Firebase — which is why
// the reviver is injected rather than fixed.

import { Timestamp } from 'firebase/firestore';
import { createAppStore } from './appStore';
import { createOverlay } from './overlay';
import { loadCore } from '../lib/core';
import { writeQueue, onQueueChange } from './writes';

let path = null;

/**
 * `{ store, overlay, onQueueChange }` — the whole read path, built once.
 *
 * `engine: 'auto'` is `idbStore`'s: IndexedDB where there is one, memory where
 * there is not, and **memory is a working studio for this session** rather than
 * a failure — a browser refusing us storage still gets reads and writes, it
 * just starts every reload with nothing stored. `appStore`'s `LOST` message
 * reports it.
 */
export function theReadPath() {
  if (path) return path;
  const store = createAppStore({
    engine: 'auto',
    reviveTimestamp: (iso) => Timestamp.fromDate(new Date(iso)),
  });
  path = {
    store,
    overlay: createOverlay({
      store,
      // **The live queue's own `records`, as a function**, not a snapshot or a
      // mirror (see `overlay.js`'s header). `writeQueue()` builds the queue on
      // first call, hence a thunk inside a thunk: nothing opens a database
      // because this file was imported.
      records: () => writeQueue().records(),
      // The other half of the same queue (#410): batches the server has taken
      // whose echo the listener has not delivered yet. Derived on every read,
      // as `records` is, because a copy could outlive the write it describes.
      confirmed: () => writeQueue().confirmed(),
      loadCore,
    }),
    onQueueChange,
  };
  return path;
}

/** Test seam. Drops the singleton so the next call builds a fresh session. */
export function __resetReadPathForTests() {
  path = null;
}
